import { useCallback, useEffect, useMemo, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Tooltip,
    Typography,
    CircularProgress,
} from '@mui/material';
import AddBoxIcon from '@mui/icons-material/AddBox';
import { ModalAssign } from '~/shared/ui/modals/modalAssign/ModalAssign';
import Dashboard from '~/features/dashboard/Dashboard';
import { CreateTemplateModal } from '~/shared/ui/modals/CreateTemplateModal';
import { ConfirmDeleteModal } from '~/shared/ui/modals/ConfirmDeleteModal';
import { EditTemplateModal } from '~/shared/ui/modals/EditTemplateModal';
import { ITemplateRow } from '~/features/templates/model/types/types';
import { IFilterConfig, IModalState, IPaginationModel } from '~/shared/types/types';
import { useDeleteTemplateCampaignMutation, useDeleteTemplateCampaignBatchMutation, useCopyTemplateCampaignMutation } from '~/features/templates/model/api/templates-api';
import { DataTable } from '~/shared/ui/tables/DataTable';
import { defaultPaginationModel } from '~/shared/const/app';
import { useTemplateCampaignQuery } from '~/features/templates/model/hooks/useTemplateCampaignQuery';
import { hasAccess } from '~/shared/config/access';
import { useSelector } from 'react-redux';
import { usersApi } from '~/features/user/model/api/users-api';
import { useSearchParams } from 'react-router-dom';
import { usePersistedTableState } from '~/shared/hooks/usePersistedTableState';

export const TemplatesPage = () => {
    const { data: me } = useSelector(usersApi.endpoints.getUserInfo.select(undefined)) as any;
    const [modalState, setModalState] = useState<IModalState>({
        open: false,
        openDeleteModal: false,
        openDeleteSelectedModal: false,
        openTemplateCreateCampaign: false,
        openTemplateEditCampaign: false,
        openTemplateCampaign: false,
    });

    const {
        activeFilters,
        setActiveFilters,
        paginationModel,
        setPaginationModel,
    } = usePersistedTableState('templates', defaultPaginationModel);

    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [allRowsMap, setAllRowsMap] = useState<Map<number, ITemplateRow>>(new Map());

    const [editingTemplate, setEditingTemplate] = useState<ITemplateRow | null>(null);
    const [deletingTemplate, setDeletingTemplate] = useState<number>(0);
    const [textPreview, setTextPreview] = useState<{ open: boolean; title: string; content: string }>({
        open: false,
        title: '',
        content: '',
    });

    const { data: responseData, error, isFetching, refetch } = useTemplateCampaignQuery(me?.roles, {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: paginationModel.search,
        filters: activeFilters,
    });

    const [deleteTemplateCampaign, { isLoading: isDeleting }] = useDeleteTemplateCampaignMutation();
    const [deleteTemplateCampaignBatch, { isLoading: isBatchDeleting }] = useDeleteTemplateCampaignBatchMutation();
    const [copyTemplateCampaign, { isLoading: isCopying }] = useCopyTemplateCampaignMutation();
    const [copyingId, setCopyingId] = useState<number | null>(null);

    const confirmDelete = async () => {
        try {
            const result = await deleteTemplateCampaign(deletingTemplate).unwrap();
            if (result) {
                handleModal('openDeleteModal', false);
                refetch();
            }
        } catch (err) {
            console.error('Ошибка удаления:', err);
        }
    };

    const confirmDeleteSelected = async () => {
        try {
            const result = await deleteTemplateCampaignBatch(selectedIds).unwrap();
            if (result) {
                handleModal('openDeleteSelectedModal', false);
                setSelectedIds([]);
                refetch();
            }
        } catch (err) {
            console.error('Ошибка массового удаления:', err);
        }
    };

    const handleModal = useCallback((key: keyof IModalState, value: boolean) => {
        setModalState(prev => ({ ...prev, [key]: value }));
    }, []);

    const rows = useMemo((): ITemplateRow[] => {
        return (responseData?.data || []).map((row: ITemplateRow) => {
            const parsed = JSON.parse(JSON.stringify(row.data) || '{}');
            return {
                ...row,
                ...parsed,
            };
        });
    }, [responseData]);

    const rowCount = responseData?.pagination?.total || 0;

    const selectedRows = useMemo(() => {
        return selectedIds.map(id => allRowsMap.get(id)).filter(Boolean) as ITemplateRow[];
    }, [selectedIds, allRowsMap]);

    useEffect(() => {
        if (!rows?.length) return;
        setAllRowsMap(prev => {
            const map = new Map(prev);
            rows.forEach((row) => map.set(row.id, row));
            return map;
        });
    }, [rows]);

    const filterColumns: IFilterConfig[] = useMemo(() => [
        {
            key: 'name',
            label: 'Название',
            type: 'text',
        },
        {
            key: 'geo',
            label: 'Гео',
            type: 'geo',
            valueKey: 'id',
        },
        {
            key: 'budget',
            label: 'Бюджет',
            type: 'text',
        },
    ], []);

    const onFilterApply = (filterState: Record<string, any>) => {
        const filters = {
            ...filterState,
            name: typeof filterState.name === 'string'
                ? filterState.name
                    .split('\n')
                    .map((s: string) => s.trim())
                    .filter(Boolean)
                : Array.isArray(filterState.name)
                    ? filterState.name.filter(Boolean)
                    : [],
        };
        setActiveFilters(filters);
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    };

    const onFilterReset = () => {
        setActiveFilters(null);
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    };

    const handleRefresh = useCallback(() => {
        refetch();
    }, [refetch]);

    const loadingText = useMemo(() => {
        if (paginationModel.search || activeFilters) {
            return "идет поиск";
        }
        return "загрузка данных";
    }, [paginationModel.search, activeFilters]);

    const columns: GridColDef[] = useMemo(
        () => [
            { field: 'id', headerName: 'ID', width: 50 },
            { 
                field: 'name', 
                headerName: 'Название', 
                minWidth: 200,
                renderCell: (params) => (
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            whiteSpace: 'normal', 
                            wordBreak: 'break-word', 
                            lineHeight: 1.5,
                            py: 1
                        }}
                    >
                        {params.value}
                    </Typography>
                )
            },
            { field: 'geo', headerName: 'Гео', width: 120, flex: 0 },
            { 
                field: 'budget', 
                headerName: 'Бюджет', 
                width: 120,
                flex: 0,
                renderCell: params => {
                    const rowBudgetFrom = Number(params.row?.budget_usd_from);
                    const rowBudgetTo = Number(params.row?.budget_usd_to);
                    if (Number.isFinite(rowBudgetFrom) && Number.isFinite(rowBudgetTo) && rowBudgetFrom >= 1 && rowBudgetTo >= rowBudgetFrom) {
                        return (
                            <Typography variant="body2">
                                {rowBudgetFrom === rowBudgetTo
                                    ? `${rowBudgetFrom} USD`
                                    : `${rowBudgetFrom}-${rowBudgetTo} USD`}
                            </Typography>
                        );
                    }

                    const budgetValues = Array.isArray(params.value)
                        ? params.value
                        : typeof params.value === 'string'
                            ? params.value
                                .split(/[\n;,]+/)
                                .map(v => v.trim())
                                .filter(Boolean)
                            : params.value != null
                                ? [String(params.value)]
                                : [];

                    const shortValues = budgetValues.slice(0, 2).join('; ');
                    const hasMoreThanTwo = budgetValues.length > 2;
                    const textValue = hasMoreThanTwo ? `${shortValues}...` : shortValues;

                    return (
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ width: '100%' }}
                            onClick={e => e.stopPropagation()}
                        >
                            <Typography variant="body2">{textValue}</Typography>
                            {hasMoreThanTwo ? (
                                <Tooltip title="Посмотреть">
                                    <IconButton
                                        size="small"
                                        sx={{ color: 'text.secondary', p: 0.5, m: 0.25 }}
                                        onClick={() =>
                                            setTextPreview({
                                                open: true,
                                                title: 'Бюджет',
                                                content: budgetValues.join('\n'),
                                            })
                                        }
                                    >
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            ) : null}
                        </Stack>
                    );
                }
            },
            { 
                field: 'headlines',
                sortable: false,
                headerName: 'Заголовки', 
                width: 80, 
                flex: 0,
                align: 'center',
                headerAlign: 'center',
                renderCell: params => {
                    const raw = params.value;
                    const lines = Array.isArray(raw)
                        ? raw
                        : typeof raw === 'string'
                            ? raw.split('\n').map((s: string) => s.trim()).filter(Boolean)
                            : [];
                    const content = Array.isArray(raw) ? lines.join('\n') : String(raw ?? '');
                    const count = lines.length || (content ? 1 : 0);

                    return (
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                            spacing={0.5}
                            sx={{ width: '100%' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Typography variant="body2">{count}</Typography>
                            {content ? (
                                <IconButton
                                    size="small"
                                    sx={{ color: 'text.secondary', p: 0.5, m: 0.25 }}
                                    onClick={() => setTextPreview({ open: true, title: 'Заголовки', content })}
                                >
                                    <VisibilityIcon fontSize="small" />
                                </IconButton>
                            ) : null}
                        </Stack>
                    );
                }
            },
            { 
                field: 'descriptions', 
                sortable: false,
                headerName: 'Описания', 
                width: 80, 
                flex: 0,
                align: 'center',
                headerAlign: 'center',
                renderCell: params => {
                    const raw = params.value;
                    const lines = Array.isArray(raw)
                        ? raw
                        : typeof raw === 'string'
                            ? raw.split('\n').map((s: string) => s.trim()).filter(Boolean)
                            : [];
                    const content = Array.isArray(raw) ? lines.join('\n') : String(raw ?? '');
                    const count = lines.length || (content ? 1 : 0);

                    return (
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                            spacing={0.5}
                            sx={{ width: '100%' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Typography variant="body2">{count}</Typography>
                            {content ? (
                                <IconButton
                                    size="small"
                                    sx={{ color: 'text.secondary', p: 0.5, m: 0.25 }}
                                    onClick={() => setTextPreview({ open: true, title: 'Описания', content })}
                                >
                                    <VisibilityIcon fontSize="small" />
                                </IconButton>
                            ) : null}
                        </Stack>
                    );
                }
            },
            {
                field: 'user_name',
                headerName: 'Пользователь',
                width: 160,
                flex: 0,
            },
            {
                field: 'actions',
                sortable: false,
                headerName: 'Действия',
                width: 150,
                renderCell: params => (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Tooltip title="Удалить">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setDeletingTemplate(params.row.id);
                                    handleModal('openDeleteModal', true);
                                }}
                                sx={{ color: 'error.main', p: 0.5, m: 0.25 }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Редактировать">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setEditingTemplate(params.row);
                                    handleModal('openTemplateEditCampaign', true);
                                }}
                                sx={{ color: 'primary.main', p: 0.5, m: 0.25 }}
                            >
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Копировать">
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={copyingId === params.row.id}
                                    onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                            setCopyingId(params.row.id);
                                            await copyTemplateCampaign({ id: params.row.id, email: me.email }).unwrap();
                                        } catch (err) {
                                            console.error('Ошибка копирования:', err);
                                        } finally {
                                            setCopyingId(null);
                                        }
                                    }}
                                    sx={{ color: copyingId === params.row.id ? 'action.disabled' : 'info.main', p: 0.5, m: 0.25 }}
                                >
                                    {copyingId === params.row.id ? <CircularProgress size={20} /> : <ContentCopyIcon />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                ),
            },
        ],
        [handleModal, copyingId, copyTemplateCampaign, me]
    );

    return (
        <Dashboard>
            {hasAccess(me?.roles ?? [], '/templates') ? (
                <Stack flexDirection="column" gap="16px">
                    <Stack direction={{ xs: 'row', lg: 'row' }} sx={{ alignItems: 'center' }}>
                        <Typography component="h2" variant="h6" sx={{ flex: 1 }}>
                            Шаблоны
                        </Typography>
                        <Stack direction="row" gap={2}>
                            <Stack sx={{ flex: 1 }} flexDirection="row" gap={2} justifyContent="end">
                                <Button variant="contained" color="primary" startIcon={<AddBoxIcon />} onClick={() => handleModal('openTemplateCampaign', true)}>
                                    Добавить шаблон
                                </Button>
                                {selectedIds.length > 0 && (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={() => handleModal('openDeleteSelectedModal', true)}
                                    >
                                        Удалить выбранные
                                    </Button>
                                )}
                                <ModalAssign
                                    open={modalState.openAssign as any}
                                    handleClose={() => handleModal('openAssign', false)}
                                    ids={selectedRows as any}
                                />
                            </Stack>
                        </Stack>
                    </Stack>

                    <DataTable
                    rows={rows}
                    columns={columns}
                    rowCount={rowCount}
                    isFetching={isFetching}
                    paginationModel={paginationModel}
                    setPaginationModel={setPaginationModel}
                    selectedIds={selectedIds}
                    setSelectedIds={setSelectedIds}
                    filterColumns={filterColumns}
                    onFilterApply={onFilterApply}
                    onFilterReset={onFilterReset}
                    loadingText={loadingText}
                    onRefresh={handleRefresh}
                    getRowHeight={() => 'auto'}
                    activeFilters={activeFilters}
                />

                    <CreateTemplateModal open={modalState.openTemplateCampaign} handleClose={() => handleModal('openTemplateCampaign', false)} />
                    <EditTemplateModal editingTemplate={editingTemplate} open={modalState.openTemplateEditCampaign} handleClose={() => handleModal('openTemplateEditCampaign', false)} />
                    <ConfirmDeleteModal 
                        open={modalState.openDeleteModal} 
                        onClose={() => handleModal('openDeleteModal', false)} 
                        onConfirm={() => confirmDelete()} 
                        header="Удалить шаблон?"
                        isLoading={isDeleting}
                    />
                    <ConfirmDeleteModal 
                        open={modalState.openDeleteSelectedModal as boolean} 
                        onClose={() => handleModal('openDeleteSelectedModal', false)} 
                        onConfirm={() => confirmDeleteSelected()} 
                        header={`Удалить выбранные шаблоны (${selectedIds.length})?`}
                        isLoading={isBatchDeleting}
                    />
                    <Dialog
                        open={textPreview.open}
                        onClose={() => setTextPreview(prev => ({ ...prev, open: false }))}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>{textPreview.title}</DialogTitle>
                        <DialogContent>
                            <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                {textPreview.content}
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setTextPreview(prev => ({ ...prev, open: false }))}>
                                Закрыть
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Stack>
            ) : (
                <Stack>Нет доступа</Stack>
            )}
        </Dashboard>
    );
};
